/**
 * ClientDetails - a contact as a canvas with panes floating over its edges.
 *
 * WHAT CHANGED, AND WHY.
 *
 * The page was a `DetailFrame` around a hand-rolled tab strip and a fixed
 * `lg:grid-cols-3` Details tab, whose facts (type, status, onboarding, email,
 * phone, address, timestamps) only existed on that tab - so they left the
 * screen the moment someone opened Matters or Documents. The audit log was a
 * `fixed end-0 top-0 h-full w-96` drawer with its own scrim, outside every
 * layout the page had. Reading a document was a modal over the whole page.
 *
 * All three are PANES on the shared `DetailPageSurface` now - they dock,
 * stack, reorder by dragging their header, resize, and are remembered per user
 * per contact. See `surface/layout`.
 *
 * THE LAYERING, outside in:
 *
 *   DetailFrame        breadcrumbs, the collaborator stack, the actions menu
 *     DetailTabBar     the canvas's tabs + the pane rail, full width
 *     DetailPageSurface  the canvas, with the panes over its edges
 *
 * WHAT STAYED A MODAL. Everything transactional: connecting a matter,
 * uploading documents, managing access, and the delete / deactivate
 * confirmations. Those are wide, one-shot, confirm-and-go flows - a pane is
 * for content you keep beside the work, not for a form you fill once and
 * dismiss. Only persistent side CONTENT moved.
 *
 * PER-TAB FILTER STATE LIVES IN THE TAB. The Matters and Documents tabs each
 * keep their own search / filters / sort, and their `FilterBarWithTabs` stays
 * inside the tab body rather than moving onto the nav bar - so the state is
 * where the controls are, and swapping tabs does not reset either.
 */

import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from '../../contexts/LayoutContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import apis from '../../utils/apis';
import { RootState, useAppDispatch } from '../../store';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { deleteClient as deleteClientAPI } from '../../store/actions/ClientActions';
import { useGlobalStreaming } from '../../hooks/useGlobalStreaming';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import Flag from '../shared/TailwindComponents/Flag';
import Table from '../shared/TailwindComponents/Table';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import { AttachmentDropzone, AttachmentList, toAttachments } from '../shared/attachments';
import DetailFrame from '../shared/Frames/DetailFrame';
import DetailTabBar, { TabItem } from '../shared/surface/DetailTabBar';
import DetailPageSurface from '../shared/surface/DetailPageSurface';
import PaneRail from '../shared/surface/PaneRail';
import PaneBody from '../shared/surface/PaneBody';
import GlassCard from '../shared/brand/GlassCard';
import { FactTile, FieldGrid, InfoRow, KpiTile } from '../shared/brand/Field';
import AccessPane from '../shared/access/AccessPane';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import RecentActivityCard from '../shared/RecentActivityCard';
import { contactTypeBadge, parentLabelsByTypeId } from './contactTypeBadge';
import { getStatusColor, formatDate, formatDateTime } from '../../utils/functions';
import {
  PiFilePlusLight,
  PiTrashLight,
  PiFileTextLight,
  PiFolderLight,
  PiNoteLight,
  PiPencilLight,
  PiCheckCircleLight,
  PiXCircleLight,
  PiFileLight,
  PiInfoLight,
  PiLinkLight,
  PiMoneyLight,
  PiPlusLight,
  PiTextAlignLeftLight,
  PiClockCounterClockwiseLight,
  PiUsersThreeLight,
  PiAddressBookLight,
  PiEnvelopeLight,
  PiPhoneLight,
  PiMapPinLight,
  PiTagLight,
  PiUserLight,
  PiUploadSimpleLight,
} from 'react-icons/pi';
import { useSelector } from 'react-redux';
import { showConnectMatterModal, showUploadClientDocumentModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import { buildClientCollaboratorAdapter } from '../../utils/collaboratorAdapters/client';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { FeatureTour, clientDetailsTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { CONTACT_SURFACE, CONTACT_TABS, ContactTab, useContactWorkspace } from './surface';
import { ContactSurfaceProvider } from './surface/ContactSurfaceContext';
import ContactContextPane from './surface/ContactContextPane';
import ContactReaderPane from './surface/ContactReaderPane';

import { getJurisdictionDisplay } from '../../utils/jurisdictionFlag';
import { toErrorText } from '../../utils/errorMessage';

const statusToColorMap: Record<string, any> = {
  Open: 'yellow',
  Active: 'blue',
  Closed: 'gray',
  Discovery: 'blue',
  Negotiation: 'blue',
  Trial: 'blue',
  Intake: 'yellow',
};

interface RouteParams {
  clientId: string;
}

interface Client {
  id: number;
  uuid: string;
  name: string;
  type_id: number;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  notes?: string;
  is_active: boolean;
  is_individual?: boolean;
  contact_person_name?: string;
  contact_person_email?: string;
  organization_id: number;
  created_at: string;
  updated_at: string;
  client_metadata?: Record<string, any>;
}

interface Matter {
  id?: number;
  matter_id?: number;
  matter_uuid: string;
  name: string;
  status: string;
  created_at: string;
  updated_at?: string;
  category?: string;
  role?: string;
  type?: string;
  added_at?: string;
}

interface AuditLogEntry {
  id: number;
  action: string;
  details: string;
  created_by_user_name: string;
  created_at: string;
  metadata?: Record<string, any>;
}

const ClientDetails: React.FC = () => {
  const { clientId } = useParams<RouteParams>();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { setIsContentScrollable } = useContext(LayoutContext);
  const dispatch = useAppDispatch();
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const currentUserId = useSelector((state: RootState) => state.auth?.userId);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const { viewFile } = useGlobalStreaming();

  const [client, setClient] = useState<Client | null>(null);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [matterSearch, setMatterSearch] = useState('');
  const [matterFilters, setMatterFilters] = useState<{ [key: string]: string[] }>({});
  const [matterSort, setMatterSort] = useState<string>('');
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentFilters, setDocumentFilters] = useState<{ [key: string]: string[] }>({});
  const [documentSort, setDocumentSort] = useState<string>('');
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [clientTypes, setClientTypes] = useState<any[]>([]);
  const [billing, setBilling] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [isEditingBilling, setIsEditingBilling] = useState(false);
  const [editedBilling, setEditedBilling] = useState<any>(null);
  const [lawyers, setLawyers] = useState<any[]>([]);
  const [selectedLawyerIds, setSelectedLawyerIds] = useState<string[]>([]);
  const [lawyerProfiles, setLawyerProfiles] = useState<Record<string, any>>({});
  const [isMatterLoading, setIsMatterLoading] = useState(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [isAuditLogLoading, setIsAuditLogLoading] = useState(false);

  /**
   * The workspace: pane layout, the reader's content and the active tab, from
   * the ONE shared hook. Keyed by contact AND by user, so each contact
   * remembers its own arrangement and nobody inherits a colleague's - see
   * `surface/layout` and `services/layout/layoutStore`.
   */
  const {
    panels,
    api: surfaceApi,
    activeTab,
    goToTab,
    readerContent,
    setReaderContent,
  } = useContactWorkspace({
    entityId: clientId,
    // The RAW param: the hook checks it against the declared tabs, so a stale
    // link (the tabs were lowercase once) lands on Details, not on nothing.
    initialTab: new URLSearchParams(location.search).get('tab') || undefined,
    onTabChange: (tab) => history.replace(`/contacts/${clientId}?tab=${tab}`),
  });

  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: 'client-details',
    steps: clientDetailsTourSteps,
    readyToShow: !isLoading && !!client,
    autoStartDelay: 600,
  });

  // Fetch client details
  const fetchClientDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}`,
      });

      if (resp.status === 200 && resp.data?.success && resp.data?.client) {
        setClient(resp.data.client);
      } else {
        throw new Error('Failed to fetch client details');
      }
    } catch (err: any) {
      dispatch(
        showFeedbackModal({
          showModal: true,
          modalType: 'error',
          message: t('Failed to fetch contact details'),
          duration: 3000,
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [clientId, dispatch, t]);

  // Fetch client types for dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await apis({
          method: 'GET',
          url: '/dropdown/client-types',
        });
        if (res && res.data) {
          let items = res.data;
          if (!Array.isArray(items)) {
            items = res.data?.data || res.data?.list || [];
          }
          if (Array.isArray(items)) {
            setClientTypes(items);
          }
        }
      } catch (err) {
      }
    })();
  }, []);

  // Fetch matters connected to this client
  const fetchMatters = useCallback(async () => {
    try {
      setIsMatterLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/matters`,
      });
      if (resp.status === 200 && resp.data?.success) {
        setMatters(resp.data.matters || []);
      }
    } catch (err) {
    } finally {
      setIsMatterLoading(false);
    }
  }, [clientId]);

  // Fetch audit log for this client
  const fetchAuditLog = useCallback(async () => {
    try {
      setIsAuditLogLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/audit-log`,
      });
      if (resp.status === 200 && resp.data?.success) {
        setAuditLog(resp.data.audit_log || []);
      }
    } catch (err) {
    } finally {
      setIsAuditLogLoading(false);
    }
  }, [clientId]);

  // Fetch billing information
  const fetchBilling = useCallback(async () => {
    try {
      setIsBillingLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/billing`,
      });
      if (resp.status === 200 && resp.data?.success) {
        setBilling(resp.data.billing);
        setEditedBilling(resp.data.billing);
      }
    } catch (err) {
    } finally {
      setIsBillingLoading(false);
    }
  }, [clientId]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setIsDocumentLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/documents`,
      });
      if (resp.status === 200 && resp.data?.success) {
        setDocuments(resp.data.documents || []);
      }
    } catch (err) {
    } finally {
      setIsDocumentLoading(false);
    }
  }, [clientId]);

  // Fetch user profile for a lawyer
  const fetchLawyerProfile = useCallback(async (lawyerUuid: string) => {
    try {
      const resp = await apis({
        method: 'GET',
        url: `/user-profile/user/uuid/${lawyerUuid}`,
      });
      if (resp.status === 200 && resp.data?.success && resp.data?.user_info) {
        setLawyerProfiles((prev) => ({
          ...prev,
          [lawyerUuid]: resp.data.user_info,
        }));
      }
    } catch (err) {
    }
  }, []);

  // Fetch lawyers for this client
  const fetchLawyers = useCallback(async () => {
    try {
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/lawyers`,
      });
      if (resp.status === 200 && resp.data?.success) {
        const lawyersData = resp.data.lawyers || [];
        setLawyers(lawyersData);
        const uuids = lawyersData.map((l: any) => l.uuid);
        setSelectedLawyerIds(uuids);

        // Fetch profiles for lawyers with missing name/email data
        for (const lawyer of lawyersData) {
          if (!lawyer.name || !lawyer.email) {
            fetchLawyerProfile(lawyer.uuid);
          }
        }
      }
    } catch (err) {
    }
  }, [clientId, fetchLawyerProfile]);


  useEffect(() => {
    if (clientId) {
      fetchClientDetails();
    }
  }, [clientId]);

  useEffect(() => {
    if (client) {
      setBreadcrumbs([
        { name: t('Contacts'), href: '/contacts', current: false },
        { name: client.name || t('Unknown Contact'), href: `/contacts/${clientId}`, current: true },
      ]);
    }
    return () => setBreadcrumbs([]);
  }, [client, clientId, setBreadcrumbs]);

  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  // Load tab-specific data on tab change
  useEffect(() => {
    if (activeTab === 'Details') {
      // Fetch lawyers and available users
      (async () => {
        try {
          await fetchLawyers();
          await fetchAuditLog();
        } catch (err) {
          console.error('Error loading details tab data:', err);
        }
      })();
    } else if (activeTab === 'Matters') {
      fetchMatters();
    } else if (activeTab === 'Documents') {
      fetchDocuments();
    } else if (activeTab === 'Billing') {
      fetchBilling();
    }
  }, [activeTab, clientId, fetchMatters, fetchDocuments, fetchBilling, fetchAuditLog]);

  /**
   * Editing moved OFF the canvas (con-3).
   *
   * The Details tab used to flip its one card into a form in place, so the
   * facts you were editing against left the screen the moment you started.
   * `ContactEditPanel` owns the form, the validation and the save now - and it
   * sends only the fields that changed, where this handler sent all nine with
   * `||` fallbacks, so clearing a phone number wrote the old one back.
   */
  const handleEditContact = useCallback(() => {
    if (!client) return;
    dispatch(showSidePanel({
      type: 'editContact',
      showSidePanel: true,
      size: 'md',
      message: t('Edit contact'),
      icon: <PiPencilLight className="w-5 h-5" />,
      client,
      contactTypes: clientTypes,
      onSaved: (saved: any) => {
        setClient((prev) => ({ ...(prev as any), ...saved }));
        fetchAuditLog();
      },
    } as any));
  }, [client, clientTypes, dispatch, fetchAuditLog, t]);

  const handleDeleteClient = useCallback(() => {
    if (!client) return;

    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        title: t('Delete Contact'),
        message: t('This contact and all associated data will be permanently deleted.'),
        subText: t('This action cannot be undone.'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        variant: 'danger',
        confirmLabel: t('Delete'),
        onConfirm: async () => {
          try {
            await deleteClientAPI(client.uuid);
            dispatch(
              showFeedbackModal({
                showModal: true,
                message: t('Contact deleted successfully'),
                modalType: 'success',
                duration: 3000,
              })
            );
            history.push('/contacts');
          } catch (error: any) {
            dispatch(
              showFeedbackModal({
                showModal: true,
                message: t('Failed to delete contact'),
                modalType: 'error',
                duration: 3000,
              })
            );
          }
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  }, [client, dispatch, t, history]);


  const handleSaveBilling = async () => {
    if (!editedBilling || !client) return;

    try {
      setIsSaving(true);
      const resp = await apis({
        method: 'PUT',
        url: `/clients/${client.uuid}/billing`,
        data: editedBilling,
      });

      if (resp.status === 200 && resp.data?.success) {
        setBilling(editedBilling);
        setIsEditingBilling(false);
        dispatch(
          showFeedbackModal({
            showModal: true,
            modalType: 'success',
            message: t('Billing information updated successfully'),
            duration: 3000,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        showFeedbackModal({
          showModal: true,
          modalType: 'error',
          message: t('Failed to update billing information'),
          duration: 3000,
        })
      );
    } finally {
      setIsSaving(false);
    }
  };


  const handleDeleteDocument = useCallback((documentUuid: string) => {
    if (!client) return;

    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        title: t('Delete Document'),
        message: t('This document will be permanently deleted.'),
        subText: t('This action cannot be undone.'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        variant: 'danger',
        confirmLabel: t('Delete'),
        onConfirm: async () => {
          try {
            await apis({
              method: 'DELETE',
              url: `/clients/${client.uuid}/documents/${documentUuid}`,
            });

            await fetchDocuments();
            dispatch(
              showFeedbackModal({
                showModal: true,
                modalType: 'success',
                message: t('Document deleted successfully'),
                duration: 3000,
              })
            );
          } catch (err: any) {
            dispatch(
              showFeedbackModal({
                showModal: true,
                modalType: 'error',
                message: t('Failed to delete document'),
                duration: 3000,
              })
            );
          }
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  }, [client, dispatch, t, fetchDocuments]);

  /**
   * Open a document in the READER PANE.
   *
   * This dispatched `showModal({ type: 'attachmentViewer' })` - a modal over
   * the whole page that covered the Documents table you opened it from. The
   * pane is beside the list instead, and it opens IMMEDIATELY with a loader so
   * the several seconds of blob fetch happen somewhere visible rather than
   * behind a dead row press.
   */
  const handleOpenDocument = useCallback(async (documentUuid: string, filename: string) => {
    if (!client) return;

    const fileType = filename.split('.').pop()?.toLowerCase() || 'file';
    surfaceApi.openReader({ title: filename, loading: true, clientUuid: client.uuid });

    try {
      const result = await viewFile({
        domain: 'clients',
        resourceId: client.uuid,
        fileId: documentUuid,
        filename: filename,
        contentType: fileType,
      });

      if (!result.success || !result.blob) {
        surfaceApi.closePane('reader');
        setReaderContent(null);
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: toErrorText(result.error) || t('Failed to load document'),
          showModal: true,
          duration: 3000,
        }));
        return;
      }

      surfaceApi.openReader({
        title: filename,
        clientUuid: client.uuid,
        citation: {
          title: filename,
          name: filename,
          blob: result.blob,
          blobObject: result.blob,
          contentType: result.contentType || fileType,
          type: 'file',
          id: documentUuid,
        },
      });
    } catch (error: any) {
      surfaceApi.closePane('reader');
      setReaderContent(null);
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error opening document. Please try again.'),
        showModal: true,
        duration: 3000,
      }));
    }
  }, [client, dispatch, viewFile, t, surfaceApi, setReaderContent]);

  const getClientTypeLabel = useCallback((typeId: number): string => {
    const clientType = clientTypes.find((ct) => ct.value === String(typeId) || ct.id === typeId);
    return clientType?.label || `Type ${typeId}`;
  }, [clientTypes]);

  /* ── The three facts the whole page keys off ──────────────────────
     A contact is a PERSON or an ORGANIZATION, and the difference decides
     whether the Contact Person block exists at all. `entity_kind` is the
     backend's word for it (feature/final-revamp-backend); `is_individual` is
     the field that has always been there, and stays the fallback. */

  const isCompany = useMemo(() => {
    if (!client) return false;
    const kind = (client as any).entity_kind;
    if (kind) return kind !== 'person';
    return client.is_individual === false;
  }, [client]);

  const isOnboarded = useMemo(
    () => !!((client as any)?.is_onboarded ?? client?.client_metadata?.IsOnboarded),
    [client]
  );

  /** The same coloured, parent-grouped badge the contacts table renders. */
  const typeBadge = useMemo(() => {
    const parents = parentLabelsByTypeId(clientTypes);
    return contactTypeBadge(
      {
        typeLabel: (client as any)?.type_label || (client ? getClientTypeLabel(client.type_id) : ''),
        parentCode: (client as any)?.parent_type_code,
        parentLabel: (client as any)?.parent_type_label || (client ? parents[client.type_id] : ''),
        entityKind: (client as any)?.entity_kind || (isCompany ? 'business' : 'person'),
      },
      t('Unspecified')
    );
  }, [client, clientTypes, getClientTypeLabel, isCompany, t]);

  /* ── The header's actions ──────────────────────────────────────────
     These were inline `onClick` bodies inside a hand-rolled Headless UI
     `Menu` in the header. The menu is `DetailFrame`'s now - same items, same
     dispatches, one implementation of the dropdown - so each one is a named
     handler. Every one of them STAYS a modal or a side panel: they are
     transactional (pick matters and connect, choose files and upload, confirm
     a status change), not content to keep beside the work. */

  const handleConnectMatter = useCallback(async () => {
    if (!client) return;
    try {
      setIsMatterLoading(true);
      const res = await apis({ method: 'GET', url: '/matters/view?page=1&limit=12' });
      let available = [];
      if (res && res.data) {
        available = Array.isArray(res.data.data)
          ? res.data.data
          : Array.isArray(res.data.matters) ? res.data.matters : [];
      }
      dispatch(showConnectMatterModal(
        client.uuid,
        client.name,
        client.uuid,
        available,
        () => {
          fetchMatters();
          dispatch(showFeedbackModal({
            showModal: true,
            modalType: 'success',
            message: t('Matter connected successfully'),
            duration: 3000,
          }));
        }
      ));
    } catch (err) {
      console.error('Error fetching matters:', err);
    } finally {
      setIsMatterLoading(false);
    }
  }, [client, dispatch, fetchMatters, t]);

  const handleCreateMatter = useCallback(() => {
    if (!client) return;
    history.push({
      pathname: '/matters',
      search: '?add=true',
      state: { clientId: client.uuid, clientName: client.name },
    });
  }, [client, history]);

  /**
   * The inline dropzone posts straight to the contact's own endpoint - the
   * same one `DocumentUploadModal` uses. The modal stays for the actions-menu
   * route (it reports a per-file success/rejection summary); this path is for
   * "drag three files onto the tab you are already looking at".
   */
  const handleUploadFiles = useCallback(async (files: File[]) => {
    if (!client || !files.length) return;
    const failed: string[] = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'general');
        const resp = await apis({
          method: 'POST',
          url: `/clients/${client.uuid}/documents`,
          data: formData,
        });
        if (!(resp.status === 200 || resp.status === 201) || !resp.data?.success) {
          failed.push(file.name);
        }
      } catch {
        failed.push(file.name);
      }
    }
    await fetchDocuments();
    dispatch(showFeedbackModal({
      showModal: true,
      modalType: failed.length ? 'error' : 'success',
      message: failed.length
        ? t('Could not upload {{files}}', { files: failed.join(', ') })
        : t('Documents uploaded successfully'),
      duration: 3000,
    }));
  }, [client, dispatch, fetchDocuments, t]);

  const handleUploadDocument = useCallback(() => {
    if (!client) return;
    dispatch(showUploadClientDocumentModal(
      client.uuid,
      client.name,
      () => {
        fetchDocuments();
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'success',
          message: t('Documents uploaded successfully'),
          duration: 3000,
        }));
      }
    ));
  }, [client, dispatch, fetchDocuments, t]);

  const handleToggleActive = useCallback(() => {
    if (!client) return;
    const willDeactivate = client.is_active !== false;
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        title: willDeactivate ? t('Deactivate this contact?') : t('Reactivate this contact?'),
        message: willDeactivate
          ? t('This contact will no longer be available for new matters.')
          : t('This contact will become available again for new matters.'),
        icon: willDeactivate
          ? <PiXCircleLight className="w-6 h-6 text-warning-600" />
          : <PiCheckCircleLight className="w-6 h-6 text-success-600" />,
        variant: willDeactivate ? 'warning' : 'success',
        confirmLabel: willDeactivate ? t('Deactivate') : t('Reactivate'),
        onConfirm: async () => {
          try {
            const resp = await apis({
              method: 'PUT',
              url: `/clients/${client.uuid}`,
              data: { is_active: !willDeactivate },
            });
            if (resp.status === 200) {
              dispatch(showFeedbackModal({
                showModal: true,
                message: willDeactivate
                  ? t('Contact deactivated successfully')
                  : t('Contact reactivated successfully'),
                modalType: 'success',
                duration: 3000,
              }));
              fetchClientDetails();
            }
          } catch (error: any) {
            dispatch(showFeedbackModal({
              showModal: true,
              message: t('Failed to update contact status'),
              modalType: 'error',
              duration: 3000,
            }));
          }
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  }, [client, dispatch, fetchClientDetails, t]);

  // Build breadcrumbs
  const clientBreadcrumbs = useMemo(() => [
    { name: t('Contacts'), href: '/contacts', current: false },
    { name: client?.name || t('Loading...'), href: `/contacts/${clientId}`, current: true },
  ], [client, clientId, t]);

  /**
   * The people on this contact, with anything the profile lookup filled in.
   *
   * `client.lawyers` sends split names and often no picture; `lawyerProfiles`
   * is the per-uuid fetch that has them. Merging here means the stack, the
   * pane and the manage panel all read one row rather than each re-deriving
   * "name or first+last or profile first+last or Person abc12345".
   */
  const accessPeople = useMemo(
    () =>
      (lawyers || []).map((lawyer: any) => {
        const profile = lawyerProfiles[lawyer.uuid] || {};
        return {
          ...lawyer,
          first_name: lawyer.first_name || profile.first_name,
          last_name: lawyer.last_name || profile.last_name,
          email: lawyer.email || profile.email,
          profile_picture: lawyer.profile_picture || profile.profile_picture || null,
        };
      }),
    [lawyers, lawyerProfiles]
  );

  /* The header faces RAISE THE ACCESS PANE. This was a third hand-rolled
     avatar stack - `CollaboratorBadge`s with their own name chain, their own
     "+N" and no way in at all (it was not even pressable). */
  const additionalHeaderElements = useMemo(() => (
    <div className="flex gap-3 items-center">
      <CollaboratorStack
        collaborators={accessPeople}
        onClick={() => surfaceApi.openPane('access')}
        testId="contact-collaborator-stack"
      />
    </div>
  ), [accessPeople, surfaceApi]);

  // Sub header with status and badges
  const subHeaderRight = useMemo(() => (
    <div className="flex flex-row gap-2 items-center">
      <Badge color={client?.is_active ? 'green' : 'gray'}>
        {client?.is_active ? t('Active') : t('Inactive')}
      </Badge>
      <Badge color={typeBadge.color}>{typeBadge.label}</Badge>
      {!isCompany ? (
        <Badge color="gray">
          {client?.contact_email || t('No Email')}
        </Badge>
      ) : (
        client?.contact_person_name && (
          <Badge color="gray">
            {client.contact_person_name}
          </Badge>
        )
      )}
    </div>
  ), [client, t, typeBadge, isCompany]);

  // Filter matters by search + filters before rendering
  const filteredMatters = useMemo(() => {
    const q = matterSearch.trim().toLowerCase();
    const statuses = matterFilters['Status'] || [];
    return matters.filter((m: any) => {
      if (q) {
        const title = (m.title || m.name || '').toLowerCase();
        const type = (m.type || '').toLowerCase();
        const category = (m.category_name || '').toLowerCase();
        if (!title.includes(q) && !type.includes(q) && !category.includes(q)) return false;
      }
      if (statuses.length > 0) {
        const status = m.status_name || (m.status === 'active' ? 'Active' : 'Open');
        if (!statuses.includes(status)) return false;
      }
      return true;
    });
  }, [matters, matterSearch, matterFilters]);

  const matterFilterSections = useMemo(() => {
    const statusSet = new Set<string>();
    matters.forEach((m: any) => {
      const status = m.status_name || (m.status === 'active' ? 'Active' : 'Open');
      statusSet.add(status);
    });
    return [
      {
        id: 'status',
        name: 'Status',
        label: t('Status'),
        options: Array.from(statusSet).map((s) => ({ value: s, label: s })),
      },
    ] as any;
  }, [matters, t]);

  const matterSortOptions = useMemo(() => [
    { value: 'name_asc', label: t('Name A-Z'), current: matterSort === 'name_asc' },
    { value: 'name_desc', label: t('Name Z-A'), current: matterSort === 'name_desc' },
    { value: 'created_desc', label: t('Newest first'), current: matterSort === 'created_desc' },
    { value: 'created_asc', label: t('Oldest first'), current: matterSort === 'created_asc' },
  ] as any, [t, matterSort]);

  // Prepare matters table data with rich formatting
  const mattersTableData = useMemo(() => {
    const sorted = [...filteredMatters];
    if (matterSort === 'name_asc' || matterSort === 'name_desc') {
      sorted.sort((a: any, b: any) => {
        const an = (a.title || a.name || '').toLowerCase();
        const bn = (b.title || b.name || '').toLowerCase();
        return matterSort === 'name_asc' ? an.localeCompare(bn) : bn.localeCompare(an);
      });
    } else if (matterSort === 'created_asc' || matterSort === 'created_desc') {
      sorted.sort((a: any, b: any) => {
        const ad = new Date(a.created_at || 0).getTime();
        const bd = new Date(b.created_at || 0).getTime();
        return matterSort === 'created_asc' ? ad - bd : bd - ad;
      });
    }
    return sorted.map((matter: any) => {
      const matterTitle = matter.title || matter.name || `Matter ${matter.id}`;
      const matterType = matter.type || t('No type');
      const jurisdictionData = getJurisdictionDisplay(matter.jurisdiction);
      const statusName = matter.status_name || (matter.status === 'active' ? 'Active' : 'Open');

      return {
        id: matter.matter_uuid || matter.uuid || matter.id,
        uuid: matter.matter_uuid || matter.uuid || matter.id,
        matter_uuid: matter.matter_uuid || matter.uuid || matter.id,
        // Raw values for client-side sorting (the cells above are rendered JSX).
        matter_name: matterTitle,
        created_at: matter.created_at,
        'Matter Name': (
          <div className="flex items-center gap-2 group/main relative w-full min-w-0 pe-4">
            {jurisdictionData && (
              <Flag countryCode={jurisdictionData.countryCode} code={jurisdictionData.code} size="lg" />
            )}
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="text-gray-900 dark:text-gray-100 truncate" title={matterTitle}>
                {matterTitle}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                {matterType}
              </div>
            </div>
          </div>
        ),
        'Category': (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {matter.category_name || t('No category')}
          </span>
        ),
        'Status': (
          <Badge
            className='capitalize'
            color={statusToColorMap[statusName] || getStatusColor(statusName) as any || 'gray'}
          >
            {statusName}
          </Badge>
        ),
        'Created At': formatDate(matter.created_at),
      };
    });
  }, [filteredMatters, matterSort, t]);

  // Filter + sort documents
  const filteredDocuments = useMemo(() => {
    const q = documentSearch.trim().toLowerCase();
    const types = documentFilters['Type'] || [];
    let result = documents.filter((d: any) => {
      if (q) {
        const fname = (d.filename || '').toLowerCase();
        const uploader = (d.uploaded_by_user_name || '').toLowerCase();
        if (!fname.includes(q) && !uploader.includes(q)) return false;
      }
      if (types.length > 0) {
        const ftype = (d.file_type || (d.filename || '').split('.').pop() || '').toUpperCase();
        if (!types.includes(ftype)) return false;
      }
      return true;
    });
    if (documentSort === 'name_asc' || documentSort === 'name_desc') {
      result = [...result].sort((a: any, b: any) => {
        const an = (a.filename || '').toLowerCase();
        const bn = (b.filename || '').toLowerCase();
        return documentSort === 'name_asc' ? an.localeCompare(bn) : bn.localeCompare(an);
      });
    } else if (documentSort === 'date_asc' || documentSort === 'date_desc') {
      result = [...result].sort((a: any, b: any) => {
        const ad = new Date(a.uploaded_at || 0).getTime();
        const bd = new Date(b.uploaded_at || 0).getTime();
        return documentSort === 'date_asc' ? ad - bd : bd - ad;
      });
    }
    return result;
  }, [documents, documentSearch, documentFilters, documentSort]);

  /**
   * The filter/sort bar above still owns which rows are shown and in what
   * order; this only normalizes the survivors into the shared shape, so
   * `sortAttachments` is deliberately NOT applied over the top of it.
   */
  const contactAttachments = useMemo(
    () => toAttachments(filteredDocuments),
    [filteredDocuments]
  );

  const documentFilterSections = useMemo(() => {
    const typeSet = new Set<string>();
    documents.forEach((d: any) => {
      const ftype = (d.file_type || (d.filename || '').split('.').pop() || '').toUpperCase();
      if (ftype) typeSet.add(ftype);
    });
    return [
      {
        id: 'type',
        name: 'Type',
        label: t('Type'),
        options: Array.from(typeSet).map((s) => ({ value: s, label: s })),
      },
    ] as any;
  }, [documents, t]);

  const documentSortOptions = useMemo(() => [
    { value: 'name_asc', label: t('Name A-Z'), current: documentSort === 'name_asc' },
    { value: 'name_desc', label: t('Name Z-A'), current: documentSort === 'name_desc' },
    { value: 'date_desc', label: t('Newest first'), current: documentSort === 'date_desc' },
    { value: 'date_asc', label: t('Oldest first'), current: documentSort === 'date_asc' },
  ] as any, [t, documentSort]);

  // Determine whether current user can administer this contact's access list.
  // Admin = current user has access_level 'admin' OR is_lead on this client,
  // OR is a firm-level admin role.
  const canAdmin = useMemo(() => {
    const ui: any = userInfo || {};
    const candidateIds = new Set(
      [
        currentUserId,
        ui.UserID,
        ui.userId,
        ui.id,
        ui.UserUUID,
        ui.user_uuid,
        ui.uuid,
      ]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .map((v) => String(v))
    );
    const me = lawyers.find((l: any) =>
      candidateIds.has(String(l.uuid)) ||
      candidateIds.has(String(l.user_id)) ||
      candidateIds.has(String(l.id))
    );
    if (me) {
      const access = (me.access_level || me.role || '') as string;
      if (access === 'admin' || access === 'full_access') return true;
      if (me.is_lead) return true;
    }
    const role = ui?.Role || ui?.role || '';
    const userType = ui?.UserType || ui?.user_type || '';
    if (userType === 'LawFirmAdmin') return true;
    if (typeof role === 'string' && /admin|managing partner|owner/i.test(role)) return true;
    return false;
  }, [lawyers, currentUserId, userInfo]);

  /**
   * Access STAYS a side panel.
   *
   * It is a transactional flow - search people, set a level, pick a lead,
   * save - and it is shared with matters, projects and tasks through
   * `buildClientCollaboratorAdapter`. The READ of it (who is on this contact)
   * is on the Details tab; this is the way to CHANGE it.
   */
  const handleManageAccess = useCallback(() => {
    const adapter = buildClientCollaboratorAdapter({
      clientId: clientId!,
      lawyers,
      currentUserUuid: currentUserId || undefined,
      canAdmin,
      onUpdate: () => { fetchLawyers(); },
      subMessage: t('Add or remove people who can access this contact.'),
    });
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage Access'),
      icon: <PiUsersThreeLight className="w-5 h-5" />,
      ...adapter,
    } as any));
  }, [dispatch, clientId, lawyers, currentUserId, canAdmin, fetchLawyers, t]);

  if (isLoading || !client) return <Skeleton variant="detail" />;

  /* The VALUES come from the surface config - one vocabulary for the bar, the
     URL and `goToTab`. Only the labels, icons and the Billing tooltip are the
     page's. */
  const TAB_LABELS: Record<ContactTab, string> = {
    Details: t('Details'),
    Matters: t('Matters'),
    Documents: t('Documents'),
    Billing: t('Billing'),
  };
  const TAB_ICONS: Record<ContactTab, React.ReactNode> = {
    Details: <PiNoteLight />,
    Matters: <PiFileTextLight />,
    Documents: <PiFolderLight />,
    Billing: <PiMoneyLight />,
  };
  const tabs: TabItem[] = CONTACT_TABS.map((value) => ({
    label: TAB_LABELS[value],
    value,
    icon: TAB_ICONS[value],
    ...(value === 'Billing' ? { disabled: true, tooltip: t('Coming soon') } : {}),
  }));

  /* The header's overflow menu - `DetailFrame`'s, not a hand-rolled one. The
     Activity item raises the PANE; everything else opens the modal or side
     panel it always did. */
  const dropdownActions = [
    {
      label: t('Edit details'),
      onClick: handleEditContact,
      icon: <PiPencilLight className="w-4 h-4" />,
    },
    ...(client.is_active
      ? [
          {
            label: t('Connect Matter'),
            onClick: handleConnectMatter,
            icon: <PiLinkLight className="w-4 h-4" />,
            disabled: isMatterLoading,
          },
          {
            label: t('Create Matter'),
            onClick: handleCreateMatter,
            icon: <PiPlusLight className="w-4 h-4" />,
          },
        ]
      : []),
    {
      label: t('Upload Document'),
      onClick: handleUploadDocument,
      icon: <PiFilePlusLight className="w-4 h-4" />,
    },
    /* NO "Manage Access" HERE - the rail's Access pill raises the pane that
       lists who is on this contact, and the pane's own button opens the
       editor. See `surface/layout`. */
    {
      label: client.is_active !== false ? t('Deactivate') : t('Reactivate'),
      onClick: handleToggleActive,
      icon: client.is_active !== false
        ? <PiXCircleLight className="w-4 h-4 text-warning-600" />
        : <PiCheckCircleLight className="w-4 h-4 text-success-600" />,
    },
    {
      label: t('Activity'),
      onClick: () => surfaceApi.openPane('activity'),
      icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
    },
    {
      label: t('Delete'),
      onClick: handleDeleteClient,
      icon: <PiTrashLight className="w-4 h-4" />,
      variant: 'destructive' as const,
    },
  ];

  return (
    <ContactSurfaceProvider value={surfaceApi}>
    <DetailFrame
      breadcrumbs={clientBreadcrumbs}
      dropdownActions={dropdownActions}
      dropdownTourId="client-actions-menu"
      subscriptionActive={subscriptionActive}
      additionalHeaderElements={additionalHeaderElements}
      showBackButton={true}
      subHeaderRight={subHeaderRight}
    >
      <div className="flex-1 flex flex-col min-h-0">
        {/*
          THE TABS ARE THEIR OWN ROW, FULL WIDTH, ABOVE THE SURFACE - so a
          docked pane floats below them rather than over them, and the row does
          not shrink as panes open.
        */}
        <div data-tour-id="client-tabs" className="flex-shrink-0 w-full">
          <DetailTabBar
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={goToTab}
            tourId="client-tabs-left"
            /* Tabs left, pane toggles right, one row. NOTE what is NOT here:
               the Matters and Documents filters. They belong with the rows
               they filter, so each stays inside its own tab body - which is
               also what keeps each tab's search / filter / sort alive across
               a tab swap. */
            actions={(
              <PaneRail
                panels={panels}
                ariaLabel={t('Panels')}
                labels="auto"
                /* Straight from the surface config: every pane that asked for
                   a rail toggle gets one, in declaration order, so a new pane
                   cannot be added and then forgotten here. */
                items={CONTACT_SURFACE.railItems({
                  t,
                  icons: {
                    context: <PiInfoLight />,
                    access: <PiUsersThreeLight />,
                    activity: <PiClockCounterClockwiseLight />,
                  },
                  // The roster's own size, so the pill is right whether or not
                  // the pane has ever been opened.
                  badges: { access: lawyers.length },
                })}
              />
            )}
          />
        </div>

        <DetailPageSurface
          surface={CONTACT_SURFACE}
          layoutApi={panels}
          panes={[
            {
              id: 'context',
              title: t('Context'),
              icon: <PiInfoLight />,
              body: (
                <ContactContextPane
                  client={client}
                  typeLabel={typeBadge.label}
                  typeColor={typeBadge.color}
                />
              ),
            },
            {
              id: 'access',
              title: t('Access'),
              icon: <PiUsersThreeLight />,
              body: (
                <AccessPane
                  collaborators={accessPeople}
                  onManageAccess={handleManageAccess}
                  canManage={canAdmin}
                  /* A contact's owner row is the LEAD - the responsible
                     lawyer - not a creator. */
                  ownerLabel="Lead"
                  emptyText={t('No people assigned yet.')}
                />
              ),
            },
            {
              id: 'activity',
              title: t('Activity'),
              icon: <PiClockCounterClockwiseLight />,
              /* A thunk: the log fetches on mount, so it is built only while
                 the pane is actually on screen. No `hideHeader` - it reads
                 `useInPane()` and renders its body alone. */
              body: () => (
                <PaneBody>
                  <AuditLogPanel
                    entityType="client"
                    entityId={client.uuid}
                    entityName={client.name || ''}
                    onClose={() => panels.setVisible('activity', false)}
                  />
                </PaneBody>
              ),
            },
            {
              id: 'reader',
              title: readerContent?.title || t('Reader'),
              icon: <PiFileLight />,
              body: () => (
                <ContactReaderPane
                  content={readerContent}
                  onClose={() => {
                    panels.setVisible('reader', false);
                    setReaderContent(null);
                  }}
                />
              ),
            },
          ]}
        >
          {/* ── Details ──────────────────────────────────────────────
              The facts that used to fill this tab are in the context pane
              now; what is left is what you EDIT or read down: the notes, who
              has access, and the last few things that happened. */}
          {activeTab === 'Details' && (
            <div
              className="details-canvas flex-1 min-h-0 overflow-y-auto custom-scrollbar-main"
              data-tour-id="client-details-tab"
            >
              <div className="details-pad mx-auto w-full max-w-6xl px-6 py-8 space-y-8">
              {/* ── 1. Hero - who this is, and the one affordance ────── */}
              <header className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1 space-y-2.5">
                  <h1 className="text-3xl font-semibold text-fg-strong leading-tight tracking-tight break-words hyphens-auto">
                    {client.name}
                  </h1>
                  {isCompany && client.contact_person_name && (
                    <p className="text-sm text-fg-muted -mt-1 truncate" title={client.contact_person_name}>
                      {t('Contact Person')}: {client.contact_person_name}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge color={client.is_active ? 'green' : 'gray'}>
                      {client.is_active ? t('Active') : t('Inactive')}
                    </Badge>
                    <Badge color={typeBadge.color}>{typeBadge.label}</Badge>
                    <Badge color="gray">
                      {isCompany ? t('Organization') : t('Individual')}
                    </Badge>
                    {isOnboarded && <Badge color="blue">{t('Onboarded')}</Badge>}
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="small"
                  className="flex-shrink-0"
                  onClick={handleEditContact}
                  icon={<PiPencilLight className="h-3.5 w-3.5" />}
                >
                  {t('Edit details')}
                </Button>
              </header>

              {/* ── 2. KPI strip - what this contact amounts to ──────── */}
              <GlassCard padding="none">
                <div className="details-kpis divide-hairline-soft">
                  <KpiTile icon={<PiFileTextLight />} label={t('Matters')} value={matters.length} />
                  <KpiTile icon={<PiFolderLight />} label={t('Documents')} value={documents.length} />
                  <KpiTile icon={<PiUsersThreeLight />} label={t('People with access')} value={accessPeople.length} />
                  <KpiTile
                    icon={<PiClockCounterClockwiseLight />}
                    label={t('Last updated')}
                    value={client.updated_at ? formatDate(client.updated_at) : '—'}
                  />
                </div>
              </GlassCard>

              {/* ── 3. Two columns ──────────────────────────────────── */}
              <div className="details-body gap-6 items-start">
                {/* ── MAIN - the fields, then the prose ── */}
                <div className="min-w-0 space-y-6">
                  {/*
                    CONTACT INFO IS FIELDS NOW, not a paragraph. This card used
                    to render `client.notes` under the heading "Contact
                    Information" - so the one section named after the contact's
                    details showed none of them, and the details themselves
                    existed only in the context pane. Same primitives as the
                    matter workspace: `FieldGrid` of `FactTile`s.
                  */}
                  <GlassCard icon={<PiAddressBookLight />} title={t('Contact Information')}>
                    <FieldGrid columns={2}>
                      <FactTile
                        icon={<PiEnvelopeLight />}
                        label={isCompany ? t('Organization Email') : t('Email')}
                        value={client.contact_email}
                      />
                      <FactTile
                        icon={<PiPhoneLight />}
                        label={t('Phone')}
                        value={client.contact_phone}
                      />
                      {isCompany && (
                        <FactTile
                          icon={<PiUserLight />}
                          label={t('Contact Person')}
                          value={client.contact_person_name}
                        />
                      )}
                      {isCompany && (
                        <FactTile
                          icon={<PiEnvelopeLight />}
                          label={t('Contact Person Email')}
                          value={client.contact_person_email}
                        />
                      )}
                      <FactTile
                        icon={<PiTagLight />}
                        label={t('Contact Type')}
                        value={typeBadge.label}
                      />
                      <FactTile
                        icon={<PiMapPinLight />}
                        label={t('Address')}
                        value={client.address}
                      />
                    </FieldGrid>
                  </GlassCard>

                  <GlassCard icon={<PiTextAlignLeftLight />} title={t('Notes')}>
                    {client.notes ? (
                      <p className="text-sm text-fg-body leading-relaxed whitespace-pre-wrap max-w-prose">
                        {client.notes}
                      </p>
                    ) : (
                      <p className="text-sm text-fg-muted">{t('No notes added')}</p>
                    )}
                  </GlassCard>
                </div>

                {/* ── SIDE - the record itself, then what happened to it ── */}
                <div className="details-side min-w-0 space-y-6">
                  <GlassCard icon={<PiInfoLight />} title={t('Record')}>
                    <div className="space-y-1">
                      <InfoRow
                        label={t('Created')}
                        value={client.created_at ? formatDateTime(client.created_at) : '—'}
                      />
                      <InfoRow
                        label={t('Last updated')}
                        value={client.updated_at ? formatDateTime(client.updated_at) : '—'}
                      />
                      <InfoRow
                        label={t('Onboarded')}
                        value={isOnboarded ? t('Yes') : t('No')}
                      />
                      <InfoRow label={t('Status')}>
                        <Badge color={client.is_active ? 'green' : 'gray'}>
                          {client.is_active ? t('Active') : t('Inactive')}
                        </Badge>
                      </InfoRow>
                    </div>
                  </GlassCard>

                  {/* NO ACCESS CARD HERE. The roster was a `GlassCard` on this
                      tab with its own row anatomy, its own `access_level`
                      mapping and its own "Manage" button - so it vanished the
                      moment someone opened Matters or Documents, which is
                      exactly when "who is on this contact" gets asked. It is
                      the shared Access PANE now: the rail pill (badged with
                      the count) raises it, and its footer button is the one
                      way to the editor. */}

                  {/* ── Recent Activity - "View all" raises the pane ── */}
                  <RecentActivityCard
                    fetchEntries={async () => auditLog.slice(0, 5).map((e: any) => ({
                      id: e.id,
                      action: e.action,
                      details: e.details,
                      created_at: e.created_at,
                      user_name: e.created_by_user_name,
                    }))}
                    onViewAll={() => surfaceApi.openPane('activity')}
                  />
                </div>
              </div>
              </div>
            </div>
          )}

          {/* ── Matters ──────────────────────────────────────────────
              The filter bar lives HERE, in the tab, with the rows it filters
              - which is also why `matterSearch` / `matterFilters` /
              `matterSort` survive a swap to Documents and back. */}
          {activeTab === 'Matters' && (
            <div
              className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main"
              data-tour-id="client-matters-tab"
            >
              {isMatterLoading ? (
                <div className="px-4 py-4">
                  <Skeleton variant="table" rows={5} />
                </div>
              ) : (
                <>
                  <FilterBarWithTabs
                    filters={matterFilterSections}
                    sortOptions={matterSortOptions}
                    selectedFilters={matterFilters}
                    onFilterChange={(name, vals) => setMatterFilters((prev) => ({ ...prev, [name]: vals }))}
                    onSortChange={setMatterSort}
                    onSearchChange={setMatterSearch}
                    sortValue={matterSort}
                    onClearAllFilters={() => { setMatterFilters({}); setMatterSearch(''); setMatterSort(''); }}
                    itemCount={mattersTableData.length}
                    totalItemCount={matters.length}
                    itemCountLabel={t('matters')}
                    noBorder
                  />

                  {mattersTableData.length > 0 ? (
                    <Table
                      columns={['Matter Name', 'Category', 'Status', 'Created At']}
                      data={mattersTableData}
                      onRowClick={(row) => history.push(`/matters/${row.matter_uuid}`)}
                      sortableColumns={['Matter Name', 'Created At']}
                      sortBy="Matter Name"
                      sortOrder="asc"
                      clientSort
                      sortAccessors={{
                        'Matter Name': (r: any) => (r.matter_name || '').toLowerCase(),
                        'Created At': (r: any) => new Date(r.created_at || 0).getTime(),
                      }}
                      resizableColumns={true}
                    />
                  ) : matters.length > 0 ? (
                    <EmptyState
                      density="compact"
                      icon={<PiFileTextLight />}
                      headline={t('No matters match your filters.')}
                      subtext={t('Try a different search or clear the filters.')}
                    />
                  ) : (
                    <EmptyState
                      icon={<PiFileTextLight />}
                      headline={t('No matters connected to this contact yet.')}
                      subtext={t('Connect an existing matter, or create a new one with this contact as a party.')}
                      button={{ label: t('Connect Matter'), onClick: handleConnectMatter }}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Documents - a row press opens the READER PANE ── */}
          {activeTab === 'Documents' && (
            <div
              className="flex-1 min-h-0 flex flex-col px-4 pb-4"
              data-tour-id="client-documents-tab"
            >
              {/*
                THE EMPTY TAB IS ONE THING, NOT THREE (con-4).

                With zero documents this rendered a full filter bar (filtering
                nothing, sorting nothing), an always-on dropzone, AND the
                attachment list's own "No documents uploaded yet" - three
                pieces of chrome around an absence, each pointing somewhere
                different. Below: one `EmptyState` with one call to action,
                and the whole area is still the drop target, so dragging files
                onto it works exactly as it did.

                The filtered-to-zero case is NOT this branch: the filter bar
                stays up and `AttachmentList` shows "No matching documents",
                because there the controls are the way out.
              */}
              {!isDocumentLoading && documents.length === 0 ? (
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center">
                  <div className="w-full max-w-lg space-y-4">
                    <EmptyState
                      density="compact"
                      icon={<PiFolderLight />}
                      headline={t('No documents on this contact yet')}
                      subtext={t('Keep engagement letters, IDs and correspondence with the contact — drop them below, or upload from here.')}
                      button={{
                        label: t('Upload documents'),
                        onClick: handleUploadDocument,
                      }}
                    />
                    {/* The drop target IS the empty state's second half - one
                        block, one message, and dragging files onto the tab
                        still works. */}
                    <AttachmentDropzone onUpload={handleUploadFiles} />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-shrink-0">
                    <FilterBarWithTabs
                      filters={documentFilterSections}
                      sortOptions={documentSortOptions}
                      selectedFilters={documentFilters}
                      onFilterChange={(name, vals) => setDocumentFilters((prev) => ({ ...prev, [name]: vals }))}
                      onSortChange={setDocumentSort}
                      onSearchChange={setDocumentSearch}
                      sortValue={documentSort}
                      onClearAllFilters={() => { setDocumentFilters({}); setDocumentSearch(''); setDocumentSort(''); }}
                      itemCount={filteredDocuments.length}
                      totalItemCount={documents.length}
                      itemCountLabel={t('documents')}
                      noBorder
                      actions={[{
                        label: t('Upload'),
                        onClick: handleUploadDocument,
                        icon: <PiUploadSimpleLight className="h-4 w-4" />,
                      }]}
                    />
                  </div>
                  <AttachmentDropzone
                    className="mb-3"
                    onUpload={handleUploadFiles}
                  />

                  <AttachmentList
                    attachments={contactAttachments}
                    loading={isDocumentLoading}
                    onOpen={(a) => handleOpenDocument(a.id, a.name)}
                    onRemove={(a) => handleDeleteDocument(a.id)}
                    emptyIcon={<PiFolderLight className="h-6 w-6 text-fg-muted" />}
                    emptyTitle={t('No matching documents')}
                    emptyHint={t('Try a different search or clear the filters.')}
                  />
                </>
              )}
            </div>
          )}

          {/* ── Billing - the tab is disabled; this is what a stale
              `?tab=Billing` lands on. No viewport-relative height: the
              surface owns the height, the tab claims the flex space. ── */}
          {activeTab === 'Billing' && (
            <div className="flex-1 min-h-0 flex items-center justify-center px-4">
              <div className="text-center max-w-md mx-auto space-y-4">
                <PiMoneyLight className="h-16 w-16 text-fg-muted mx-auto" />
                <h3 className="text-lg font-semibold text-fg-strong">
                  {t('Billing Coming Soon')}
                </h3>
                <p className="text-sm text-fg-body">
                  {t('The billing feature is currently in development and will be available soon. Check back later for billing and invoicing management.')}
                </p>
              </div>
            </div>
          )}
        </DetailPageSurface>
      </div>

      <FeatureTour
        steps={clientDetailsTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </DetailFrame>
    </ContactSurfaceProvider>
  );
};

export default ClientDetails;
